import {
  MutableRefObject,
  useEffect,
  useLayoutEffect,
  useSyncExternalStore,
} from 'react';
import RowVirtualizationService, {
  RowVirtualizationRange,
} from './RowVirtualizationService';

const OVERSCAN = 5;
const RECOMPUTE_MARGIN = 2;
// Caps rows rendered per frame, including the cold-start fallback before virtualization starts.
export const MAX_RENDERED_ROWS = 60;

export const computeRowVirtualizationRange = ({
  bodyTop,
  viewportHeight,
  rowHeight,
  totalCount,
}: {
  bodyTop: number;
  viewportHeight: number;
  rowHeight: number;
  totalCount: number;
}): RowVirtualizationRange => {
  // bodyTop = body.getBoundingClientRect().top: negative if scrolled past.
  const scrolledPast = Math.max(0, -bodyTop);
  const visibleBottom = Math.max(0, viewportHeight - bodyTop);

  const startIndex = Math.max(
    0,
    Math.floor(scrolledPast / rowHeight) - OVERSCAN,
  );
  const endIndex = Math.min(
    totalCount,
    startIndex + MAX_RENDERED_ROWS,
    Math.ceil(visibleBottom / rowHeight) + OVERSCAN,
  );

  return { startIndex, endIndex };
};

const subscribe = (callback: () => void) => {
  const subscription = RowVirtualizationService.subscribe(callback);
  return () => subscription.unsubscribe();
};

export const useRowVirtualizationRange = (): RowVirtualizationRange =>
  useSyncExternalStore(subscribe, RowVirtualizationService.getRange);

export const useRowVirtualizationOrchestrator = ({
  bodyWrapperRef,
  totalCount,
  rowHeight,
}: {
  bodyWrapperRef: MutableRefObject<HTMLElement | null>;
  totalCount: number;
  rowHeight: number;
}): RowVirtualizationRange => {
  useLayoutEffect(() => {
    if (!rowHeight || !totalCount) {
      return undefined;
    }

    let rafId: number | null = null;
    let lastStart = -RECOMPUTE_MARGIN - 1;
    let lastEnd = -RECOMPUTE_MARGIN - 1;

    const recompute = () => {
      const el = bodyWrapperRef.current;
      if (!el) return;

      const { top: bodyTop } = el.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      if (viewportHeight === 0) return;

      const { startIndex, endIndex } = computeRowVirtualizationRange({
        bodyTop,
        viewportHeight,
        rowHeight,
        totalCount,
      });

      if (
        Math.abs(startIndex - lastStart) >= RECOMPUTE_MARGIN ||
        Math.abs(endIndex - lastEnd) >= RECOMPUTE_MARGIN
      ) {
        lastStart = startIndex;
        lastEnd = endIndex;
        RowVirtualizationService.setRange({ startIndex, endIndex });
      }
    };

    const scheduleRecompute = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        recompute();
      });
    };

    recompute();

    const scrollSource: EventTarget =
      document.getElementById('app-page-content') ?? window;
    scrollSource.addEventListener('scroll', scheduleRecompute, {
      passive: true,
    });
    window.addEventListener('resize', scheduleRecompute);

    return () => {
      scrollSource.removeEventListener('scroll', scheduleRecompute);
      window.removeEventListener('resize', scheduleRecompute);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
    // Ref is stable; .current read inside the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount, rowHeight]);

  useEffect(
    () => () => {
      RowVirtualizationService.reset();
    },
    [],
  );

  return useRowVirtualizationRange();
};
